import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@shared/components/notifications';
import { domainQueryKeys } from './queryKeys';
import {
  InstituteAdminTableData,
  InstituteAdminFormData,
  RemoveInstituteAdminParams,
} from '../types';
import { useAuthContext } from '@shared/components';
import { userInvitationApi, InviteUserWithRoleDTO } from '../../user-invitation';
import {
  useUsersWithRoles,
  UserRole,
  UserWithRoleDTO,
  userManagementQueryKeys,
} from '../../user-management';
import { domainApi } from '../api/domainApi';

// Query hook for fetching institute admins by domain
export const useInstituteAdmins = (tenantId: string, domainId: string) => {
  const { data, isLoading, isError, error, refetch, isFetching } = useUsersWithRoles(
    {
      role: UserRole.FACULTY_ADMIN,
      tenantId,
      domainId,
    },
    {
      enabled: !!(tenantId && domainId),
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
    }
  );

  // Transform UserWithRoleDTO[] to match expected InstituteAdminTableData format
  const transformedData: InstituteAdminTableData[] = data.map((userWithRole: UserWithRoleDTO) => {
    const instituteAdminRole = userWithRole.roles?.find(
      (role) => role.role === UserRole.FACULTY_ADMIN
    );
    return {
      id: userWithRole.id,
      name: userWithRole.name,
      email: userWithRole.email,
      role: UserRole.FACULTY_ADMIN,
      assignedAt: instituteAdminRole?.assignedAt || userWithRole.createdAt,
      assignedBy: instituteAdminRole?.assignedBy || '',
      userRoleId: instituteAdminRole?.userRoleId || userWithRole.id,
      userId: userWithRole.id,
      tenantId: userWithRole.tenantId,
      domainId: domainId,
      expiresAt: instituteAdminRole?.expiresAt,
      isExpired: instituteAdminRole?.isExpired || false,
      status: userWithRole.status,
    };
  });

  return {
    data: transformedData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  };
};

// Mutation hook for inviting institute admin user
export const useInviteInstituteAdmin = (tenantId: string, domainId: string) => {
  const queryClient = useQueryClient();
  const { getCurrentUserId } = useAuthContext();

  return useMutation({
    mutationFn: async (data: InstituteAdminFormData) => {
      // Convert to unified DTO
      const unifiedData: InviteUserWithRoleDTO = {
        email: data.email,
        name: data.name,
        role: 'FACULTY_ADMIN',
        tenantId,
        domainId,
        assignedBy: getCurrentUserId(),
      };

      const response = await userInvitationApi.inviteUserWithRole(unifiedData);
      return response.data;
    },
    onSuccess: (result) => {
      // Invalidate legacy domain queries
      queryClient.invalidateQueries({
        queryKey: domainQueryKeys.instituteAdminsByDomain(tenantId, domainId),
      });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byDomain(domainId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId, domainId }),
      });

      if (result?.success) {
        notificationService.success(result.message || 'Institute Admin invited successfully');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to invite Institute Admin';
      notificationService.error(errorMessage);
    },
  });
};

// Mutation hook for removing institute admin role
export const useRemoveInstituteAdmin = (tenantId: string, domainId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, userRoleId, userName }: RemoveInstituteAdminParams) => {
      await domainApi.instituteAdminManagement.removeInstituteAdminRole(
        userId,
        tenantId,
        domainId,
        userRoleId
      );
      return { userId, userRoleId, userName };
    },
    onSuccess: ({ userRoleId, userName }) => {
      // Remove from institute admins list for this domain
      queryClient.setQueryData<InstituteAdminTableData[]>(
        domainQueryKeys.instituteAdminsByDomain(tenantId, domainId),
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter((admin) => admin.userRoleId !== userRoleId);
        }
      );

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byDomain(domainId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId, domainId }),
      });

      notificationService.success(`Institute Admin role removed from ${userName}`);
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to remove Institute Admin role';
      notificationService.error(errorMessage);
    },
  });
};
